/**
 * Wire shape of the body POSTed to voicerun-completions-proxy /v1/completions.
 *
 * Hand-rolled to mirror voicerun-completions-proxy/src/routes.py
 * `CompletionRequestBody` and voicerun-python `ChatCompletionRequest`. The
 * proxy stamps a scrubbed copy of this body on the trace span as
 * `completion.request` (api_key and fallbacks[].api_key removed).
 *
 * If you change the Pydantic models in either repo, update this file too.
 * No code-gen pipeline yet — see PR thread for proposed JSON-Schema
 * round-trip if drift becomes painful.
 */

export interface CompletionRequestMessage {
  role: "system" | "user" | "assistant" | "tool" | string;
  content?: string;
  // Provider-specific extras (tool_calls, name, etc.) pass through.
  [key: string]: unknown;
}

export interface CompletionRequestToolFunction {
  name: string;
  description?: string;
  parameters?: Record<string, unknown>;
}

export interface CompletionRequestTool {
  type: "function" | string;
  function: CompletionRequestToolFunction;
}

export interface CompletionRequestStreamOptions {
  stream_sentences?: boolean;
  clean_sentences?: boolean;
  min_sentence_length?: number;
  punctuation_marks?: string[];
  punctuation_language?: string;
}

export interface CompletionRequestFallback {
  provider?: string;
  model?: string;
  // api_key is stripped from completion.request before stamping; never present
  // on the trace blob even if the wire body originally carried one.
  [key: string]: unknown;
}

export interface CompletionRequest {
  provider: string;
  model: string;
  messages: CompletionRequestMessage[];
  streaming?: boolean;
  temperature?: number;
  max_tokens?: number;
  tools?: CompletionRequestTool[];
  tool_choice?: string;
  timeout?: number;
  response_schema?: Record<string, unknown>;
  stream_options?: CompletionRequestStreamOptions;
  retry?: Record<string, unknown>;
  fallbacks?: CompletionRequestFallback[];
  provider_kwargs?: Record<string, unknown>;
}

/** Try to parse the JSON-string completion.request span attribute. */
export const parseCompletionRequest = (raw: unknown): CompletionRequest | null => {
  if (typeof raw !== "string" || !raw) return null;
  try {
    const parsed = JSON.parse(raw);
    if (parsed && typeof parsed === "object" && typeof parsed.provider === "string" && typeof parsed.model === "string") {
      return parsed as CompletionRequest;
    }
  } catch {
    // fall through
  }
  return null;
};

/**
 * Wire shape stamped by the proxy on completion.response. Mirrors the
 * proxy's _build_response_for_trace output: provider/model/finish_reason
 * reflect the *winning* attempt (after fallbacks), message is the
 * assistant's serialized output (content + tool_calls), usage is the raw
 * provider usage shape.
 */
export interface CompletionResponseMessage {
  role: string;
  content?: string | null;
  tool_calls?: unknown[];
  [key: string]: unknown;
}

export interface CompletionResponse {
  provider?: string | null;
  model?: string | null;
  finish_reason?: string;
  usage?: Record<string, unknown>;
  message?: CompletionResponseMessage | null;
}

export const parseCompletionResponse = (raw: unknown): CompletionResponse | null => {
  if (typeof raw !== "string" || !raw) return null;
  try {
    const parsed = JSON.parse(raw);
    if (parsed && typeof parsed === "object") {
      return parsed as CompletionResponse;
    }
  } catch {
    // fall through
  }
  return null;
};
